/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import update from 'immutability-helper';
import { tableActions, baseTableReducer } from 'carbon-addons-iot-react';

import { getLeafIdFromHierarchyPath } from 'hierarchyUtils';
import {
  GET_ASSET_RESOURCES,
  GET_ASSET_RESOURCES_FAILURE,
  GET_ASSET_RESOURCES_SUCCESS,
  ASSET_RESOURCES_TABLE_CLEAR_SEARCH,
} from 'assetResourcesActionTypes';
import { ARCHIVE_HIERARCHY_ITEM_SUCCESS } from 'archivesActionCreators';

export const ASSET_RESOURCES_TABLE_MAX_PAGE_SIZE = 100;
export const ASSET_RESOURCES_TABLE_MIN_PAGE_SIZE = 20;
const ASSET_RESOURCES_TABLE_PAGE_SIZES = [
  ASSET_RESOURCES_TABLE_MIN_PAGE_SIZE,
  50,
  ASSET_RESOURCES_TABLE_MAX_PAGE_SIZE,
];

export const SEARCHABLE_FIELD = 'name';

export const assetResourcesInitialState = {
  table: {
    data: {},
    fetchingData: false,
    fetchDataError: null,
    tableState: {
      instanceId: 'assetResourcesTable',
      data: [],
      view: {
        filters: [],
        pagination: {
          pageSizes: ASSET_RESOURCES_TABLE_PAGE_SIZES,
          pageSize: ASSET_RESOURCES_TABLE_MIN_PAGE_SIZE,
          page: 1,
          totalItems: 0,
          maxPages: 10000000,
        },
        table: {
          isSelectAllSelected: false,
          sort: undefined,
          selectedIds: [],
          expandedIds: [],
          shouldApplyDynamicOrdering: true,
          loadingState: {
            isLoading: false,
            rowCount: ASSET_RESOURCES_TABLE_MIN_PAGE_SIZE, // page size
          },
          ordering: [
            {
              columnId: 'name',
              isHidden: false,
            },
            {
              columnId: 'description',
              isHidden: false,
            },
            {
              columnId: 'siteName',
              isHidden: false,
            },
            {
              columnId: 'parentLocationName',
              isHidden: false,
            },
            {
              columnId: 'assetTypeName',
              isHidden: false,
            },
          ],
        },
        toolbar: {
          batchActions: [],
          search: {
            defaultValue: undefined,
          },
        },
      },
    },
  },
  initialTableSearchDefaultValue: undefined,
};

export const assetResourcesReducer = (state = assetResourcesInitialState, action) => {
  switch (action.type) {
    case GET_ASSET_RESOURCES_SUCCESS: {
      return update(state, {
        table: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
          data: { $set: action.payload },
          tableState: {
            // we need to pass a set of IDs for the table to the baseTableReducer
            // so that the "select all" button can know which rows to select.  this
            // data-of-ids should track with the data rows rendered in the table itself.
            data: {
              $set: action.payload.results.map(i => ({
                id: `${i.siteUUId}:${i.uuid}`,
              })),
            },
            view: {
              pagination: {
                totalItems: { $set: action.payload.totalCount },
              },
            },
          },
        },
      });
    }
    case GET_ASSET_RESOURCES:
      return {
        ...state,
        table: {
          ...state.table,
          fetchingData: true,
          fetchDataError: null,
        },
      };
    case GET_ASSET_RESOURCES_FAILURE:
      return {
        ...state,
        table: {
          ...state.table,
          fetchingData: false,
          fetchDataError: action.payload,
        },
      };

    case ASSET_RESOURCES_TABLE_CLEAR_SEARCH:
      return update(state, {
        table: {
          tableState: {
            view: {
              filters: { $set: [] },
              toolbar: {
                search: { $set: { defaultValue: undefined } },
              },
            },
          },
        },
        initialTableSearchDefaultValue: { $set: undefined },
      });

    case tableActions.TABLE_SEARCH_APPLY:
      if (
        typeof action.instanceId === 'string' &&
        action.instanceId !== state.table.tableState.instanceId
      ) {
        return state;
      }
      return update(state, {
        initialTableSearchDefaultValue: { $set: action.payload },
      });

    case tableActions.TABLE_FILTER_CLEAR:
      if (
        typeof action.instanceId === 'string' &&
        action.instanceId !== state.table.tableState.instanceId
      ) {
        return state;
      }
      return update(state, {
        table: {
          tableState: {
            view: {
              filters: {
                $set: [],
              },
              pagination: {
                page: { $set: 1 },
              },
              toolbar: {
                search: {
                  defaultValue: { $set: '' },
                  value: { $set: undefined },
                },
              },
            },
          },
        },
      });
    case ARCHIVE_HIERARCHY_ITEM_SUCCESS:
      return {
        ...state,
        table: {
          ...state.table,
          data: {
            ...state.table.data,
            results: state.table.data.results.filter(
              asset => asset.uuid !== getLeafIdFromHierarchyPath(action.payload)
            ),
          },
        },
      };

    default: // no-op - might be a table action
  }

  // To support instance matching (i.e. multiple table states in one store), just ensure
  // that a instanceId is provided on state passed to baseTableReducer.  If no instanceId
  // is provided, all matching actions will be processed by this table reducer.
  if (
    typeof action.instanceId === 'string' &&
    action.instanceId !== state.table.tableState.instanceId
  ) {
    return state;
  }

  switch (action.type) {
    case tableActions.TABLE_ACTION_CANCEL:
    case tableActions.TABLE_ACTION_APPLY:
    case tableActions.TABLE_ROW_CLICK:
    case tableActions.TABLE_COLUMN_SORT:
    case tableActions.TABLE_ROW_SELECT_ALL:
    case tableActions.TABLE_ROW_SELECT:
    case tableActions.TABLE_TOOLBAR_TOGGLE:
    case tableActions.TABLE_FILTER_APPLY:
    case tableActions.TABLE_EMPTY_STATE_ACTION:
    case tableActions.TABLE_COLUMN_ORDER:
    case tableActions.TABLE_ROW_EXPAND:
    case tableActions.TABLE_PAGE_CHANGE:
      return {
        ...state,
        table: {
          ...state.table,
          tableState: baseTableReducer(state.table.tableState, action),
        },
      };
    default:
      return state;
  }
};

export const ROOT_REDUCER_STATE = 'assetResources';

// selectors
export const getAssets = state => get(state, `${ROOT_REDUCER_STATE}.table.data.results`) || [];
export const getAssetsBookmark = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.data.bookmark`) || null;
export const getAssetsTotalCount = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.data.totalCount`) || null;
export const isFetchingAssets = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.fetchingData`) !== false;
export const fetchAssetsError = state => get(state, `${ROOT_REDUCER_STATE}.table.fetchDataError`);
export const getAssetTableView = state => get(state, `${ROOT_REDUCER_STATE}.table.tableState.view`);
export const getAssetTableFilters = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.tableState.view.filters`);
export const getAssetTableSearch = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.tableState.view.toolbar.search`);

export const getAssetTableSort = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.tableState.view.table.sort`);
export const getAssetTableSelectedRowIds = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.tableState.view.table.selectedIds`);
export const getAssetTableInstanceId = state =>
  get(state, `${ROOT_REDUCER_STATE}.table.tableState.instanceId`);
export const getAssetTableData = state => get(state, `${ROOT_REDUCER_STATE}.table.data`);
export const getInitialTableSearchDefaultValue = state =>
  get(state, `${ROOT_REDUCER_STATE}.initialTableSearchDefaultValue`);
